// ── 로그인 페이지 ──────────────────────────────────────────────────────────────
// 다크모드: CSS 변수 토큰 사용 (index.css .dark{} 섹션에서 자동 전환)
//   다크 배경 그라데이션은 앱 다크(차콜)와 맞춘 #1a1a1d → #0b0b0d (2026-09-28, 전: 남색 #0f172a → 보라 #1e1b4b)
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiClient';
import { useDarkMode } from '../hooks/useDarkMode';
import { useSiteInfo } from '../hooks/useSiteInfo';
import { markReturningVisitor } from '../utils/returningVisitor';
import { setLastScreenOwner, takeLastScreen } from '../utils/lastScreen';

export default function LoginPage() {
  const navigate = useNavigate();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;

  const [userId, setUserId] = useState('');
  const [userPw, setUserPw] = useState('');
  // 처음엔 안내 문구 없이 비워 둔다(2026-09-28 사용자: "당연히 아이디와 비밀번호 입력해야지"). 오류·진행 메시지만 칸이 뜬다.
  const [resultMessage, setResultMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  // 머리의 사이트 이름·배경 로고 = 장부의 운영 테넌트 간판(/api/site-info — 미팅링크 / (주)파인그룹). 예전엔 'PINE GROUP' 글자(10-28 8-1)와
  //   파인그룹 마크 그림(10-29 8-5)이 박혀 있었다. 받기 전·실패 땐 비워 둔다 — 다른 사이트 이름·로고가 잠깐 비치지 않게.
  const { name: siteName, logo: siteLogo } = useSiteInfo();
  // 로고 배지 글자 = 사이트 이름 첫 글자('(주)'·'㈜'·'주식회사' 앞머리는 빼고 — (주)파인그룹 → 파)
  const siteInitial = [...siteName.replace(/^(\(주\)|㈜|주식회사)\s*/, '')][0] || '';

  async function handleLogin() {
    // 빈값 요청을 미리 차단해서 불필요한 API 호출을 줄입니다.
    if (!userId.trim() || !userPw.trim()) {
      setResultMessage('아이디와 비밀번호를 모두 입력해 주세요.');
      setIsError(true);
      return;
    }

    try {
      setIsLoading(true);
      setIsError(false);
      setResultMessage('로그인 요청 중...');

      const result = await apiClient.post('/api/login', {
        userId: userId.trim(),
        userPw,
      });
      const data = result.data || {};

      if (result.ok) {
        const accessToken = data?.tokens?.accessToken || '';
        if (!accessToken) {
          setResultMessage('로그인 응답에 토큰이 없습니다. 잠시 후 다시 시도해 주세요.');
          setIsError(true);
          return;
        }
        localStorage.setItem('accessToken', accessToken);
        markReturningVisitor(); // 다음부터 첫 주소에서 소개 페이지 대신 로그인 화면 (로그아웃해도 유지)

        if (data?.user?.mustChangePassword) {
          setResultMessage('초기 비밀번호 상태입니다. 로그인 후 비밀번호를 변경해 주세요.');
        } else {
          setResultMessage(data?.message || '로그인 성공');
        }

        // 서버의 최신 권한 상태를 기준으로 라우팅을 결정합니다.
        const meResult = await apiClient.get('/api/me');
        const meUser = meResult?.ok ? (meResult.data?.user || {}) : (data?.user || {});
        const role = meUser.role || '';
        // 마지막 작업 화면(2026-10-26) — 이 기기에서 이 계정이 마지막으로 본 왼쪽 메뉴 화면이 있으면 거기로(없으면 아래 기본)
        setLastScreenOwner(meUser.userId);
        const lastScreen = takeLastScreen(meUser.userId);
        if (lastScreen) { navigate(lastScreen); return; }
        if (role === 'admin') {
          navigate(isMobileViewport ? '/admin/meeting-map' : '/admin/users');
        } else if (meUser.isTenantAdmin) {
          // 테넌트 관리자: 메인 회사 일반 직원관리(테넌트 스코프). '테넌트 관리'는 운영자 전용.
          navigate('/admin/users');
        } else {
          // 일반 직원: SITUATION_BOARD 권한 있으면 첫 페이지 = 상황판 (PC/모바일 동일)
          const permRes = await apiClient.get('/api/my/permissions');
          const permCodes = permRes?.ok && Array.isArray(permRes.data?.permCodes) ? permRes.data.permCodes : [];
          if (permCodes.includes('SITUATION_BOARD')) navigate('/my/board');
          else navigate('/dashboard');
        }
        return;
      }

      setIsError(true);
      if (result.status === 400) setResultMessage(`입력 오류: ${data.message || '요청 값을 확인해 주세요.'}`);
      else if (result.status === 401) setResultMessage(`인증 실패: ${data.message || '아이디 또는 비밀번호를 확인해 주세요.'}`);
      else if (result.status === 403) setResultMessage(`접근 제한: ${data.message || '권한이 없습니다.'}`);
      else if (result.status === 429) setResultMessage(`로그인 막힘: ${data.message || '잠시 후 다시 시도해 주세요.'}`);
      else setResultMessage(`요청 실패(${result.status}): ${data.message || '잠시 후 다시 시도해 주세요.'}`);
    } catch (error) {
      setIsError(true);
      setResultMessage(`네트워크 오류: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key === 'Enter' && !isLoading) handleLogin();
  }

  // ── safe-area 패딩은 env() 사용이 필요해 inline style 유지 ──
  const pageStyle = {
    padding: 'max(12px, env(safe-area-inset-top)) max(12px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left))',
  };

  return (
    <div
      style={pageStyle}
      className="
        relative isolate min-h-dvh w-full overflow-x-hidden
        flex items-center justify-center
        bg-[radial-gradient(circle_at_10%_10%,_#e0f2fe_0%,_#f8fafc_45%,_#eef2ff_100%)]
        dark:bg-[radial-gradient(circle_at_10%_10%,_#1a1a1d_0%,_#0b0b0d_60%,_#0b0b0d_100%)]
        [-webkit-text-size-adjust:100%]
      "
    >
      {/* ── 배경 워터마크 로고 = 이 사이트 로고(장부). 등록된 로고가 없으면 배경 그림 없음.
            다크는 색 뒤집기 — 흰 바탕이 있는 로고(파인 아이콘)도 네모가 비치지 않고 옅은 밝은 모양만 남게 ── */}
      {siteLogo && (
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none z-0"
        aria-hidden="true"
      >
        <img
          src={siteLogo}
          alt=""
          className="opacity-[0.07] dark:opacity-[0.04] grayscale dark:invert"
          style={{ width: 'min(72vw, 560px)', maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain', transform: 'translateY(-4%)' }}
        />
      </div>
      )}

      {/* ── 다크모드 토글 버튼 (우상단 고정) ── */}
      <button
        type="button"
        onClick={toggleDark}
        aria-label={isDark ? '라이트 모드로 전환' : '다크 모드로 전환'}
        className="
          absolute top-4 right-4 z-20
          w-9 h-9 rounded-xl
          flex items-center justify-center
          bg-[var(--bg-card)] border border-[var(--border)]
          text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--text-muted)]
          shadow-sm transition-colors duration-150 cursor-pointer
        "
      >
        {isDark ? (
          // 라이트 모드 아이콘 (해)
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"/>
            <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
        ) : (
          // 다크 모드 아이콘 (달)
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        )}
      </button>

      {/* ── 로그인 카드 ── */}
      <div
        className="
          relative z-10
          bg-white dark:bg-[var(--bg-card)]
          border border-[var(--border)]
          rounded-2xl
          shadow-[0_10px_40px_rgba(15,23,42,0.10)] dark:shadow-[0_10px_40px_rgba(0,0,0,0.4)]
          w-full
        "
        style={{ maxWidth: 'min(100%, 360px)', padding: 'clamp(20px, 5vw, 28px)' }}
      >
        {/* ── 헤더 ── */}
        <div className="mb-5">
          {/* 로고 배지 + 회사명 */}
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-9 h-9 rounded-xl bg-pine-900 flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-white text-sm font-black tracking-tight">{siteInitial}</span>
            </div>
            <span className="text-xs font-bold text-pine-900 dark:text-accent-300 tracking-wider uppercase">
              {siteName}
            </span>
          </div>
          <h1
            className="m-0 font-bold text-[var(--text)] tracking-tight leading-tight"
            style={{ fontSize: 'clamp(21px, 6vw, 26px)' }}
          >
            직원관리 시스템
          </h1>
          <p className="mt-1.5 mb-0 text-[13px] text-[var(--text-sub)]">
            계정 정보를 입력해 로그인하세요.
          </p>
        </div>

        {/* ── 폼 ── */}
        <div className="grid gap-2.5">
          {/* 아이디 */}
          <div className="grid gap-1">
            <label htmlFor="userId" className="text-[11px] font-bold text-[var(--text-sub)] tracking-wide uppercase">
              아이디
            </label>
            <input
              id="userId"
              type="text"
              placeholder="아이디 입력"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              // iOS Safari: 16px 미만이면 포커스 시 자동 확대됨 → 16px 고정 필수
              style={{ fontSize: 16 }}
              className="
                border border-[var(--border)] rounded-xl px-3 py-2.5
                bg-[var(--bg-subtle)] dark:bg-[var(--bg-subtle)]
                text-[var(--text)] placeholder:text-[var(--text-muted)]
                focus:outline-none focus:ring-2 focus:ring-pine-900/30 dark:focus:ring-accent-500/30
                focus:border-pine-900 dark:focus:border-accent-500
                transition-colors w-full [--autofill-bg:var(--bg-subtle)] [--autofill-ring:var(--tw-ring-shadow)]
              "
            />
          </div>

          {/* 비밀번호 */}
          <div className="grid gap-1">
            <label htmlFor="userPw" className="text-[11px] font-bold text-[var(--text-sub)] tracking-wide uppercase">
              비밀번호
            </label>
            <input
              id="userPw"
              type="password"
              placeholder="비밀번호 입력"
              value={userPw}
              onChange={(e) => setUserPw(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
              // iOS Safari: 16px 미만이면 포커스 시 자동 확대됨 → 16px 고정 필수
              style={{ fontSize: 16 }}
              className="
                border border-[var(--border)] rounded-xl px-3 py-2.5
                bg-[var(--bg-subtle)] dark:bg-[var(--bg-subtle)]
                text-[var(--text)] placeholder:text-[var(--text-muted)]
                focus:outline-none focus:ring-2 focus:ring-pine-900/30 dark:focus:ring-accent-500/30
                focus:border-pine-900 dark:focus:border-accent-500
                transition-colors w-full [--autofill-bg:var(--bg-subtle)] [--autofill-ring:var(--tw-ring-shadow)]
              "
            />
          </div>

          {/* 로그인 버튼 — 비밀번호 칸과 29px = 부제목 글자→"아이디" 글자의 **눈에 보이는** 간격과 같게
              (글자 상자 간격은 20px 이지만 글자 위아래 여유 때문에 보이는 간격은 29px. 칸 사이 10px + mt 19px, 2026-09-28 사용자 요청) */}
          <button
            type="button"
            onClick={handleLogin}
            disabled={isLoading}
            className="
              mt-[19px] w-full py-2.5 px-4 rounded-xl
              bg-pine-900 dark:bg-accent-600 hover:bg-pine-800 dark:hover:bg-accent-500
              text-white text-[13px] font-bold tracking-wide
              border border-transparent
              shadow-sm
              disabled:opacity-70 disabled:cursor-not-allowed
              transition-colors duration-150 cursor-pointer
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-900/40
            "
          >
            {isLoading ? '로그인 중...' : '로그인'}
          </button>
        </div>

        {/* ── 결과 메시지 박스 — 보여줄 메시지가 있을 때만 ── */}
        {resultMessage && (
        <div
          className={[
            'mt-3.5 rounded-xl px-3.5 py-2.5 text-[13px] min-h-[42px] flex items-center overflow-wrap-anywhere',
            isError
              ? 'bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-400'
              : 'bg-[var(--bg-subtle)] border border-[var(--border)] text-[var(--text-sub)]',
          ].join(' ')}
        >
          {resultMessage}
        </div>
        )}
      </div>
    </div>
  );
}
